import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import { capitalize } from 'lib/util';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

import StatusBadge from '../StatusBadge';

type Props = {
  link: LocationEpisodeLink;
  label: string;
  showRemove?: boolean;
  showPending?: boolean;
  isArchived?: boolean;
  onRemove?: () => void;
};

export default function RecordCard({
  link,
  label,
  showRemove = false,
  showPending = false,
  isArchived = false,
  onRemove,
}: Props) {
  const { locationEpisode } = link;
  const { patient, owner, group } = locationEpisode;

  return (
    <Card $isArchived={isArchived}>
      <CardHeader>
        <HeaderTitle>Client Record {label}</HeaderTitle>
        {showRemove && <RemoveLink onClick={onRemove}>Remove</RemoveLink>}
        {showPending && <StatusBadge status='pending' />}
      </CardHeader>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Patient Name</FieldLabel>
          <FieldValue>{patient.name}</FieldValue>
        </FieldContent>
        <ViewLink>View Patient Story</ViewLink>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>DOB</FieldLabel>
          <FieldValue>{patient.dateOfBirth ? simpleDate(patient.dateOfBirth) : '-'}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Sex</FieldLabel>
          <FieldValue>{capitalize(patient.sex) || '-'}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow>
        <FieldContent>
          <FieldLabel>Client</FieldLabel>
          <FieldValue>{owner.name}</FieldValue>
        </FieldContent>
      </FieldRow>

      <FieldRow $noBorder>
        <FieldContent>
          <FieldLabel>Location</FieldLabel>
          <FieldValue>{group.name}</FieldValue>
        </FieldContent>
      </FieldRow>
    </Card>
  );
}

const Card = styled.div<{ $isArchived?: boolean }>`
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.$isArchived ? 0.5 : 1)};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const HeaderTitle = styled(Body)`
  font-weight: 700;
  font-size: 14px;
  margin: 0;
`;

const RemoveLink = styled.button`
  background: none;
  border: none;
  color: ${colors.accentRed};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const FieldRow = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: ${(props) => (props.$noBorder ? 'none' : `1px solid ${colors.black10}`)};
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 12px;
`;

const FieldLabel = styled(BodySmall)`
  font-weight: 700;
  font-size: 12px;
  margin: 0;
`;

const FieldValue = styled(Body)`
  font-size: 12px;
  margin: 0;
`;

const ViewLink = styled.span`
  color: ${colors.primaryBlue};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;
